import { useEffect, useMemo, useState } from 'react';

import {
  DownloadOutlined,
  FileExcelOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Drawer, message, Space, Tooltip } from 'antd';
import { FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';

import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';

import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { PDFViewer } from 'components/PDFViewer';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { ActSverkiService } from 'services/ActSverki';
import { DocumentsService } from 'services/DocumentsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { base64toBlob, downloadDocs } from 'utils/files';
import { ActSverkiForm } from './ActSverkiForm';
import { ActSverkiTable } from './ActSverkiTable';

const PAGE_TITLE = 'Акт сверки';

function ActSverki() {
  useDocumentTitle(PAGE_TITLE);

  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [actSverkiData, setActSverkiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const tribe_id = useTribe();
  const [clients, setClients] = useState([]);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    start_date: null,
    begin_date: null,
    f_sum: undefined,
    t_sum: undefined,
    type: null,
  });

  const {
    state: { activeRecord, formIsActive, drawerVisible },
    closeDrawer,
    openDrawer,
    openCreateForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    totalRows,
    perPage,
    setTotalRows,
    setPagination,
    resetPagination,
  } = usePagination();

  const { client_id: selectedClient } = filters;

  const handleDownloadPDF = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      await downloadDocs(
        recordTitle,
        `system/media/${activeRecord?.file}/`,
        'arraybuffer',
        'application/pdf'
      );
    } catch (error) {
      message.error('Не удалось скачать PDF файл');
    } finally {
      hide();
    }
  };

  const recordTitle = useMemo(() => {
    if (activeRecord && activeRecord?.file) {
      return decodeURI(
        activeRecord?.file.split('/')[activeRecord.file.split('/').length - 1]
      );
    }
    return '';
  }, [activeRecord]);

  const selectedDocumentSrc = useMemo(() => {
    if (activeRecord) {
      return `${process.env.REACT_APP_HOSTNAME}/api/system/media/${activeRecord?.file}/`;
    }
    return '';
  }, [activeRecord]);

  const selectedClientInfo = useMemo(() => {
    if (clients?.length > 0) {
      const client = clients.find(
        (item) => item?.id === Number(selectedClient)
      );

      if (client) {
        return {
          full_name: client?.full_name,
          iin: client?.bin_iin,
          leader: client?.leader,
          address: client?.address_legal,
          nds: client?.nds,
        };
      }
    }

    return null;
  }, [selectedClient, clients]);

  const handleFormClose = () => {
    closeForm();
  };

  useEffect(() => {
    fetchActs();
  }, [page, perPage, filters]);

  const fetchActs = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await ActSverkiService.getActSverki(
        page,
        perPage,
        filters,
        tribe_id
      );
      setCount(total);
      setActSverkiData(items);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const handleDownloadExcel = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const data = await DocumentsService.generateExcel(activeRecord?.id);

      const blob = await base64toBlob(
        data,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;'
      );

      const blobUrl = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = decodeURI(
        `Акт сверки ${activeRecord.contractor_name}.xlsx`
      );
      link.click();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      message.error('Не удалось скачать Excel документ');
    } finally {
      hide();
    }
  };

  const drawerActions = (
    <Space>
      {activeRecord?.file && (
        <>
          <Tooltip placement="bottomLeft" title="Скачать Excel файл">
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleDownloadExcel}
            />
          </Tooltip>
          <Tooltip placement="bottomLeft" title="Скачать PDF файл">
            <Button icon={<DownloadOutlined />} onClick={handleDownloadPDF} />
          </Tooltip>
        </>
      )}
    </Space>
  );

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          clientId={clientId}
          pageTitle={PAGE_TITLE}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
          onClientsChange={setClients}
        />

        <AccessControl pageId="act_of_reconcilation" action="create">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              openCreateForm();
            }}
            disabled={!selectedClient}
          >
            Создать
          </Button>
        </AccessControl>
      </ActionsWrapper>

      <ActSverkiTable
        items={actSverkiData}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
        onResetFilter={resetFilters}
        fetch={fetchActs}
      />

      <ActSverkiForm
        isActive={formIsActive}
        initialValues={activeRecord}
        clientId={Number(selectedClient) || activeRecord?.client_id}
        clientInfo={selectedClientInfo}
        onClose={handleFormClose}
        onSuccess={fetchActs}
      />

      <Drawer
        title="Акт Сверки"
        width="70%"
        onClose={closeDrawer}
        onRowClick={openDrawer}
        open={drawerVisible}
        extra={drawerActions}
      >
        <PDFViewer file={selectedDocumentSrc} />
      </Drawer>
    </>
  );
}

export { ActSverki };
